import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Typography,
    TextField,
    Button,
    CircularProgress,
    IconButton,
    Box
} from '@mui/material';
import { Close, CloudUpload, Image as ImageIcon } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from './SnackbarContext';
import config from '../config';
import axios from 'axios';

const useStyles = makeStyles(() => ({
    rootContainer: {
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'white',
        fontFamily: 'Nunito, Poppins, Sansation, sans-serif',
        position: 'relative',
    },
    legalCard: {
        width: '100%',
        maxWidth: 360,
        margin: '16px auto',
        background: 'white',
        overflow: 'visible',
        borderRadius: 30,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    },
    backButton: {
        position: 'absolute',
        top: 20,
        left: 20,
        background: 'none',
        border: 'none',
        color: '#C084FC',
        fontSize: 16,
        fontFamily: 'Nunito',
        fontWeight: '600',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        '&:hover': {
            color: '#724F96',
        },
    },
    title: {
        width: 275,
        left: 37,
        top: 77,
        position: 'absolute',
        color: '#C084FC',
        fontSize: 30.26,
        fontFamily: 'Poppins',
        fontWeight: '700',
        lineHeight: '30.75px',
        wordWrap: 'break-word',
    },
    inputContainer: {
        position: 'absolute',
        overflow: 'hidden',
    },
    inputField: {
        width: '100%',
        height: 51,
        '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            border: '1.04px #262626 solid',
            height: '100%',
            '& fieldset': {
                border: '1.04px #262626 solid',
            },
            '&:hover fieldset': {
                border: '1.04px #262626 solid',
            },
            '&.Mui-focused fieldset': {
                border: '1.04px #262626 solid',
            },
        },
        '& .MuiInputBase-input': {
            padding: '15px 16px',
            fontSize: 16,
            fontFamily: 'Nunito',
            color: '#262626',
            height: 'auto',
            lineHeight: 'normal',
        },
    },
    label: {
        position: 'absolute',
        color: '#A1A1A1',
        fontSize: 18,
        fontFamily: 'Nunito',
        fontWeight: '500',
        wordWrap: 'break-word',
        pointerEvents: 'none',
        transition: 'all 0.2s ease',
    },
    labelFocused: {
        color: '#C084FC',
        fontSize: 14,
        top: -8,
        left: 16,
        background: 'white',
        padding: '0 4px',
    },
    uploadContainer: {
        width: '100%',
        padding: 12,
        background: '#F7F7F7',
        borderRadius: 12,
        outline: '1px #E5E5E5 solid',
        outlineOffset: '-1px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 6,
        cursor: 'pointer',
        minHeight: 80,
        '&:hover': {
            background: '#F0F0F0',
        },
    },
    uploadContent: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 8,
    },
    uploadIcon: {
        width: 24,
        height: 24,
        position: 'relative',
        overflow: 'hidden',
        background: '#E5E5E5',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&::before': {
            content: '"+"',
            fontSize: 16,
            color: '#A3A3A3',
            fontWeight: 'bold',
        },
    },
    uploadText: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 2,
    },
    uploadButton: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 6,
    },
    uploadLabel: {
        color: '#0EA5E9',
        fontSize: 10,
        fontFamily: 'Inter',
        fontWeight: '600',
        lineHeight: 14,
        wordWrap: 'break-word',
    },
    uploadType: {
        color: '#525252',
        fontSize: 10,
        fontFamily: 'Inter',
        fontWeight: '600',
        lineHeight: 14,
        wordWrap: 'break-word',
    },
    uploadHint: {
        color: '#A3A3A3',
        fontSize: 9,
        fontFamily: 'Inter',
        fontWeight: '400',
        lineHeight: 12,
        wordWrap: 'break-word',
    },
    submitButton: {
        width: '100%',
        height: 51,
        display: 'block',
        borderRadius: 10.40,
        border: 'none',
        color: 'white',
        fontSize: 18,
        fontFamily: 'Poppins',
        fontWeight: '700',
        transition: 'all 0.3s ease',
        '&:hover': {
            transform: 'translateY(-2px)',
        },
    },
    errorText: {
        color: '#f44336',
        fontSize: 14,
        textAlign: 'center',
        marginTop: 8,
        fontFamily: 'Nunito',
    },
    loadingSpinner: {
        color: 'white',
    },
}));

const CompanyLegalDetails = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const location = useLocation();
    const { showSnackbar } = useSnackbar();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [focusedField, setFocusedField] = useState('');

    const { propertyData, hotelId } = location.state || {};

    // Cleanup preview URLs on component unmount
    useEffect(() => {
        return () => {
            Object.values(filePreviews).forEach(url => {
                if (url) {
                    URL.revokeObjectURL(url);
                }
            });
        };
    }, []);

    const [formData, setFormData] = useState({
        gstNo: '',
        panNo: '',
        licenseCertificateNo: ''
    });

    const [files, setFiles] = useState({
        gstFile: null,
        panFile: null,
        licenseFile: null
    });

    const [filePreviews, setFilePreviews] = useState({
        gstFile: null,
        panFile: null,
        licenseFile: null
    });

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        setError('');
    };

    const handleFileUpload = (fileType, file) => {
        setFiles(prev => ({
            ...prev,
            [fileType]: file
        }));
        
        // Create preview URL
        const previewUrl = URL.createObjectURL(file);
        setFilePreviews(prev => ({
            ...prev,
            [fileType]: previewUrl
        }));
        
        setError('');
    };

    const convertFileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                // Remove the data:image/jpeg;base64, prefix
                const base64 = reader.result.split(',')[1];
                resolve(base64);
            };
            reader.onerror = error => reject(error);
        });
    };

    const removeFile = (fileType) => {
        // Clean up preview URL
        if (filePreviews[fileType]) {
            URL.revokeObjectURL(filePreviews[fileType]);
        }
        
        setFiles(prev => ({
            ...prev,
            [fileType]: null
        }));
        
        setFilePreviews(prev => ({
            ...prev,
            [fileType]: null
        }));
    };

    const handleFileInputChange = (fileType, event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file size (1MB limit)
            if (file.size > 1024 * 1024) {
                setError('File size must be less than 1MB');
                return;
            }
            // Validate file type
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
            if (!allowedTypes.includes(file.type)) {
                setError('Only JPG, JPEG, PNG files are allowed');
                return;
            }
            handleFileUpload(fileType, file);
        }
    };

    // Form validation
    const isFormValid = () => {
        return (
            formData.gstNo.trim() &&
            formData.panNo.trim() &&
            formData.licenseCertificateNo.trim() &&
            files.gstFile &&
            files.panFile &&
            files.licenseFile
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Check if form is valid
        if (!isFormValid()) {
            setError('Please fill in all required fields and upload all required documents');
            return;
        }

        setIsLoading(true);

        try {
            // Convert files to base64
            const gstImageBase64 = await convertFileToBase64(files.gstFile);
            const panImageBase64 = await convertFileToBase64(files.panFile);
            const licenseImageBase64 = await convertFileToBase64(files.licenseFile);

            // Create JSON payload with base64 images
            const payload = {
                gst_no: formData.gstNo,
                pan_no: formData.panNo,
                license_certificate_no: formData.licenseCertificateNo,
                gst_image: gstImageBase64,
                pan_image: panImageBase64,
                license_image: licenseImageBase64,
                hotel_id: hotelId
            };

            // Submit legal details
            const response = await axios.post(`${config.API_BASE_URL}/hotels/legal-details`, payload, {
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (response.data) {
                showSnackbar('Legal details submitted successfully!', 'success');
                // Navigate to email verification
                navigate('/email-verification', { 
                    state: { 
                        propertyData,
                        legalData: formData,
                        hotelId,
                        email: propertyData.email
                    } 
                });
            }
        } catch (error) {
            console.error('Error submitting legal details:', error);
            setError(error.response?.data?.error || 'Failed to submit legal details');
            showSnackbar('Failed to submit legal details', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleBack = () => {
        navigate('/property-information');
    };

    return (
        <div className={classes.rootContainer}>
            <div className={classes.legalCard}>
                {/* Back Button */}
                <button className={classes.backButton} onClick={handleBack}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-arrow-left"><path d="m12 19-7-7 7-7"/><path d="m19 12H5"/></svg>
                    Back
                </button>

                {/* Title */}
                <div className={classes.title}>
                    Company Legal Details
                </div>

                {/* Scrollable Content */}
                <div style={{ flex: 1, overflow: 'auto', padding: '0 37px', marginTop: 150 }}>
                    <form onSubmit={handleSubmit}>
                    {/* GST No. */}
                    <div style={{ marginBottom: 20, position: 'relative' }}>
                        <TextField
                            className={classes.inputField}
                            fullWidth
                            variant="outlined"
                            placeholder="GST No."
                            value={formData.gstNo}
                            onChange={(e) => handleInputChange('gstNo', e.target.value)}
                            disabled={isLoading}
                        />
                    </div>

                    {/* PAN No. */}
                    <div style={{ marginBottom: 20, position: 'relative' }}>
                        <TextField
                            className={classes.inputField}
                            fullWidth
                            variant="outlined"
                            placeholder="PAN No."
                            value={formData.panNo}
                            onChange={(e) => handleInputChange('panNo', e.target.value)}
                            disabled={isLoading}
                        />
                    </div>

                    {/* License Certificate No. */}
                    <div style={{ marginBottom: 20, position: 'relative' }}>
                        <TextField
                            className={classes.inputField}
                            fullWidth
                            variant="outlined"
                            placeholder="License Certificate No."
                            value={formData.licenseCertificateNo}
                            onChange={(e) => handleInputChange('licenseCertificateNo', e.target.value)}
                            disabled={isLoading}
                        />
                    </div>

                    {/* Upload GST */}
                    <div style={{ marginBottom: 20 }}>
                        {files.gstFile ? (
                            // Show uploaded file preview
                            <Box sx={{
                                border: '2px solid #4caf50',
                                borderRadius: '10px',
                                padding: '15px',
                                background: '#f8fff8',
                                position: 'relative'
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <ImageIcon sx={{ color: '#4caf50', fontSize: '24px' }} />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography sx={{ 
                                            fontWeight: 'bold', 
                                            color: '#2e7d32',
                                            fontSize: '14px'
                                        }}>
                                            GST Document Uploaded
                                        </Typography>
                                        <Typography sx={{ 
                                            color: '#666', 
                                            fontSize: '12px' 
                                        }}>
                                            {files.gstFile.name}
                                        </Typography>
                                    </Box>
                                    <IconButton 
                                        size="small" 
                                        onClick={() => removeFile('gstFile')}
                                        sx={{ color: '#f44336' }}
                                    >
                                        <Close />
                                    </IconButton>
                                </Box>
                                {filePreviews.gstFile && (
                                    <Box sx={{ 
                                        marginTop: '10px', 
                                        textAlign: 'center' 
                                    }}>
                                        <img 
                                            src={filePreviews.gstFile} 
                                            alt="GST Preview" 
                                            style={{ 
                                                maxWidth: '100%', 
                                                maxHeight: '150px', 
                                                borderRadius: '5px',
                                                border: '1px solid #ddd'
                                            }} 
                                        />
                                    </Box>
                                )}
                            </Box>
                        ) : (
                            // Show upload button
                            <div className={classes.uploadContainer}>
                                <input
                                    type="file"
                                    accept=".jpg,.jpeg,.png"
                                    onChange={(e) => handleFileInputChange('gstFile', e)}
                                    style={{ display: 'none' }}
                                    id="gst-upload"
                                    disabled={isLoading}
                                />
                                <label htmlFor="gst-upload" style={{ cursor: 'pointer', width: '100%' }}>
                                    <div className={classes.uploadContent}>
                                        <div className={classes.uploadIcon}></div>
                                        <div className={classes.uploadText}>
                                            <div className={classes.uploadButton}>
                                                <div className={classes.uploadLabel}>Click to upload</div>
                                                <div className={classes.uploadType}>Upload GST</div>
                                            </div>
                                            <div className={classes.uploadHint}>JPG, JPEG, PNG less than 1MB</div>
                                        </div>
                                    </div>
                                </label>
                            </div>
                        )}
                    </div>

                    {/* Upload PAN */}
                    <div style={{ marginBottom: 20 }}>
                        {files.panFile ? (
                            // Show uploaded file preview
                            <Box sx={{
                                border: '2px solid #4caf50',
                                borderRadius: '10px',
                                padding: '15px',
                                background: '#f8fff8',
                                position: 'relative'
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <ImageIcon sx={{ color: '#4caf50', fontSize: '24px' }} />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography sx={{ 
                                            fontWeight: 'bold', 
                                            color: '#2e7d32',
                                            fontSize: '14px'
                                        }}>
                                            PAN Document Uploaded
                                        </Typography>
                                        <Typography sx={{ 
                                            color: '#666', 
                                            fontSize: '12px' 
                                        }}>
                                            {files.panFile.name}
                                        </Typography>
                                    </Box>
                                    <IconButton 
                                        size="small" 
                                        onClick={() => removeFile('panFile')}
                                        sx={{ color: '#f44336' }}
                                    >
                                        <Close />
                                    </IconButton>
                                </Box>
                                {filePreviews.panFile && (
                                    <Box sx={{ 
                                        marginTop: '10px', 
                                        textAlign: 'center' 
                                    }}>
                                        <img 
                                            src={filePreviews.panFile} 
                                            alt="PAN Preview" 
                                            style={{ 
                                                maxWidth: '100%', 
                                                maxHeight: '150px', 
                                                borderRadius: '5px',
                                                border: '1px solid #ddd'
                                            }} 
                                        />
                                    </Box>
                                )}
                            </Box>
                        ) : (
                            // Show upload button
                            <div className={classes.uploadContainer}>
                                <input
                                    type="file"
                                    accept=".jpg,.jpeg,.png"
                                    onChange={(e) => handleFileInputChange('panFile', e)}
                                    style={{ display: 'none' }}
                                    id="pan-upload"
                                    disabled={isLoading}
                                />
                                <label htmlFor="pan-upload" style={{ cursor: 'pointer', width: '100%' }}>
                                    <div className={classes.uploadContent}>
                                        <div className={classes.uploadIcon}></div>
                                        <div className={classes.uploadText}>
                                            <div className={classes.uploadButton}>
                                                <div className={classes.uploadLabel}>Click to upload</div>
                                                <div className={classes.uploadType}>Upload PAN</div>
                                            </div>
                                            <div className={classes.uploadHint}>JPG, JPEG, PNG less than 1MB</div>
                                        </div>
                                    </div>
                                </label>
                            </div>
                        )}
                    </div>

                    {/* Upload License */}
                    <div style={{ marginBottom: 20 }}>
                        {files.licenseFile ? (
                            // Show uploaded file preview
                            <Box sx={{
                                border: '2px solid #4caf50',
                                borderRadius: '10px',
                                padding: '15px',
                                background: '#f8fff8',
                                position: 'relative'
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <ImageIcon sx={{ color: '#4caf50', fontSize: '24px' }} />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography sx={{ 
                                            fontWeight: 'bold', 
                                            color: '#2e7d32',
                                            fontSize: '14px'
                                        }}>
                                            License Document Uploaded
                                        </Typography>
                                        <Typography sx={{ 
                                            color: '#666', 
                                            fontSize: '12px' 
                                        }}>
                                            {files.licenseFile.name}
                                        </Typography>
                                    </Box>
                                    <IconButton 
                                        size="small" 
                                        onClick={() => removeFile('licenseFile')}
                                        sx={{ color: '#f44336' }}
                                    >
                                        <Close />
                                    </IconButton>
                                </Box>
                                {filePreviews.licenseFile && (
                                    <Box sx={{ 
                                        marginTop: '10px', 
                                        textAlign: 'center' 
                                    }}>
                                        <img 
                                            src={filePreviews.licenseFile} 
                                            alt="License Preview" 
                                            style={{ 
                                                maxWidth: '100%', 
                                                maxHeight: '150px', 
                                                borderRadius: '5px',
                                                border: '1px solid #ddd'
                                            }} 
                                        />
                                    </Box>
                                )}
                            </Box>
                        ) : (
                            // Show upload button
                            <div className={classes.uploadContainer}>
                                <input
                                    type="file"
                                    accept=".jpg,.jpeg,.png"
                                    onChange={(e) => handleFileInputChange('licenseFile', e)}
                                    style={{ display: 'none' }}
                                    id="license-upload"
                                    disabled={isLoading}
                                />
                                <label htmlFor="license-upload" style={{ cursor: 'pointer', width: '100%' }}>
                                    <div className={classes.uploadContent}>
                                        <div className={classes.uploadIcon}></div>
                                        <div className={classes.uploadText}>
                                            <div className={classes.uploadButton}>
                                                <div className={classes.uploadLabel}>Click to upload</div>
                                                <div className={classes.uploadType}>Upload License</div>
                                            </div>
                                            <div className={classes.uploadHint}>JPG, JPEG, PNG less than 1MB</div>
                                        </div>
                                    </div>
                                </label>
                            </div>
                        )}
                    </div>

                    </form>
                </div>

                {/* Fixed Submit Button at Bottom */}
                <div style={{ padding: '20px 37px', borderTop: '1px solid #f0f0f0' }}>
                    {/* Form Status Message */}
                    {!isFormValid() && !isLoading && (
                        <div style={{ 
                            marginBottom: '15px',
                            padding: '10px',
                            background: '#fff3cd',
                            border: '1px solid #ffeaa7',
                            borderRadius: '5px',
                            fontSize: '14px',
                            color: '#856404',
                            textAlign: 'center'
                        }}>
                            <strong>Form not ready:</strong> Please fill in all required fields and upload all documents to enable submission
                        </div>
                    )}
                    
                    <button
                        type="button"
                        className={classes.submitButton}
                        onClick={handleSubmit}
                        disabled={isLoading || !isFormValid()}
                        style={{
                            background: isFormValid() 
                                ? 'linear-gradient(90deg, #C084FC 0%, #724F96 100%)'
                                : 'linear-gradient(90deg, rgba(192, 132, 252, 0.50) 0%, rgba(114.29, 78.57, 150, 0.50) 100%)',
                            boxShadow: isFormValid() 
                                ? '0px 9px 14px rgba(192, 132, 252, 0.30)'
                                : '0px 9px 14px rgba(192, 132, 252, 0.20)',
                            opacity: isFormValid() ? 1 : 0.5,
                            cursor: isFormValid() ? 'pointer' : 'not-allowed',
                        }}
                        onMouseEnter={(e) => {
                            if (isFormValid()) {
                                e.target.style.background = 'linear-gradient(90deg, rgba(192, 132, 252, 0.80) 0%, rgba(114.29, 78.57, 150, 0.80) 100%)';
                                e.target.style.transform = 'translateY(-2px)';
                                e.target.style.boxShadow = '0px 12px 20px rgba(192, 132, 252, 0.40)';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (isFormValid()) {
                                e.target.style.background = 'linear-gradient(90deg, #C084FC 0%, #724F96 100%)';
                                e.target.style.transform = 'translateY(0px)';
                                e.target.style.boxShadow = '0px 9px 14px rgba(192, 132, 252, 0.30)';
                            }
                        }}
                    >
                        {isLoading ? (
                            <CircularProgress size={20} className={classes.loadingSpinner} />
                        ) : (
                            'Submit'
                        )}
                    </button>
                </div>

                {/* Error Message */}
                {error && (
                    <div style={{ 
                        padding: '20px 37px',
                        textAlign: 'center'
                    }}>
                        <Typography className={classes.errorText}>
                            {error}
                        </Typography>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CompanyLegalDetails;
